import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSentry } from '../hooks/useSentry';
import { useDemoMode } from '../hooks/useDemoMode';
import * as Sentry from '@sentry/react';
import { 
  signIn, 
  signUp, 
  signOut, 
  getCurrentUser, 
  fetchUserAttributes,
  confirmSignUp,
  resendSignUpCode,
  fetchAuthSession,
  AuthError,
  // MFA functions
  setUpTOTP,
  verifyTOTPSetup,
  updateMFAPreference,
  fetchMFAPreference,
  confirmSignIn,
  // Password reset functions
  resetPassword,
  confirmResetPassword,
  // Federated sign-in
  signInWithRedirect
} from 'aws-amplify/auth';
import MixpanelService, { EventName } from '../mixpanel';
import { setAuthCookie, clearAuthCookie } from '../utils/crossDomainAuth';

// Types
export interface User {
  id: string;
  email: string;
  name?: string;
  username: string;
  groups?: string[];
  picture?: string;
}

export interface ImpersonationContext {
  isImpersonating: boolean;
  targetUser?: User;
  originalAdminUser?: User;
  expiresAt?: number; // Timestamp when session expires
}

interface StoredImpersonationSession {
  targetUser: User;
  originalAdminUser: User;
  expiresAt: number;
  startedAt: number;
}

// Constants for localStorage
const IMPERSONATION_STORAGE_KEY = 'kolega_impersonation_session';
const IMPERSONATION_DURATION_MS = 30 * 60 * 1000; // 30 minutes

// Centralized impersonation utility - SINGLE SOURCE OF TRUTH
export const ImpersonationManager = {
  // Get current impersonation user ID (for API headers and WebSocket params)
  getCurrentImpersonationUserId(): string | null {
    try {
      const stored = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
      if (!stored) return null;
      
      const session: StoredImpersonationSession = JSON.parse(stored);

      // Check if session has expired - return null but DON'T clear localStorage
      // Let stopImpersonation handle the cleanup after retrieving original admin
      if (Date.now() > session.expiresAt) {
        return null;
      }
      
      return session.targetUser.id;
    } catch (error) {
      console.error('Error reading impersonation session:', error);
      return null;
    }
  },

  // Check if currently impersonating
  isImpersonating(): boolean {
    return this.getCurrentImpersonationUserId() !== null;
  },

  // Get full stored session - returns even if expired (for cleanup purposes)
  getStoredSession(): StoredImpersonationSession | null {
    try {
      const stored = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
      if (!stored) return null;

      const session: StoredImpersonationSession = JSON.parse(stored);
      return session; // Return even if expired - let caller decide what to do
    } catch (error) {
      console.error('Error reading impersonation session from localStorage:', error);
      return null;
    }
  },

  // Store session
  setSession(session: StoredImpersonationSession): void {
    try {
      localStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(session));
    } catch (error) {
      console.error('Error storing impersonation session to localStorage:', error);
    }
  },

  // Clear session
  clearSession(): void {
    try {
      localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing impersonation session from localStorage:', error);
    }
  },

  // Get remaining time
  getRemainingTime(): number {
    const session = this.getStoredSession();
    if (!session) return 0;
    return Math.max(0, session.expiresAt - Date.now());
  },

  // Get expiry date
  getExpiryDate(): Date | null {
    const session = this.getStoredSession();
    if (!session) return null;
    return new Date(session.expiresAt);
  }
};

interface AuthContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<{ requiresConfirmation: boolean }>;
  confirmRegistration: (email: string, code: string) => Promise<void>;
  resendConfirmation: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
  getAccessToken: () => Promise<string | null>;
  displayName: () => string | null; // Helper to get a human-friendly display name
  error: string | null;
  clearError: () => void;
  success: string | null; // Success message
  clearSuccess: () => void; // Function to clear success message
  needsConfirmation: string | null; // Email of user who needs confirmation
  updateNeedsConfirmation: (email: string | null) => void; // Function to update needsConfirmation state
  
  // Impersonation functionality
  impersonationContext: ImpersonationContext;
  startImpersonation: (targetUserId: string, targetUser: User) => void;
  stopImpersonation: () => void;
  isAdmin: boolean;
  getImpersonationTimeRemaining: () => number; // Returns milliseconds remaining
  getImpersonationExpiryTime: () => Date | null; // Returns expiry date
  
  // MFA functionality
  mfaChallenge: {
    isRequired: boolean;
    challengeType?: 'TOTP' | 'SMS';
  } | null;
  verifyMFACode: (code: string) => Promise<void>;
  setupTOTP: () => Promise<{ secretKey: string; qrCodeUri: string }>;
  completeTOTPSetup: (code: string) => Promise<void>;
  getMFAStatus: () => Promise<{ enabled: boolean; preferred: boolean }>;
  disableMFA: () => Promise<void>;
  
  // Password reset functionality
  requestPasswordReset: (email: string) => Promise<void>;
  confirmPasswordReset: (email: string, code: string, newPassword: string) => Promise<void>;
  clearMFAChallenge: () => void;
  
  // Federated Sign-In functionality
  signInWithProvider: (provider: 'Google') => Promise<void>;
  
  // WebSocket management for impersonation changes
  registerWebSocketDisconnectCallback: (callback: () => void) => () => void; // Returns unregister function
}

// Create the context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Context provider component
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [needsConfirmation, setNeedsConfirmation] = useState<string | null>(null);
  
  // Initialize Sentry hooks
  const { captureError, addBreadcrumb, setUser: setSentryUser, clearUser: clearSentryUser } = useSentry();
  
  // Initialize demo persistence for migration after auth
  const { hasPendingDemoMigration, migratePendingDemoSession } = useDemoMode();
  
  // MFA state
  const [mfaChallenge, setMfaChallenge] = useState<{
    isRequired: boolean;
    challengeType?: 'TOTP' | 'SMS';
  } | null>(null);
  
  // Impersonation state with localStorage persistence
  const [impersonationContext, setImpersonationContext] = useState<ImpersonationContext>({
    isImpersonating: false,
  });
  const [impersonationTimer, setImpersonationTimer] = useState<NodeJS.Timeout | null>(null);
  const [impersonationCheckInterval, setImpersonationCheckInterval] = useState<NodeJS.Timeout | null>(null);
  
  // WebSocket disconnect callbacks for impersonation changes
  const webSocketDisconnectCallbacks = useRef<Set<() => void>>(new Set());
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Effect to update Sentry user context when auth state changes
  useEffect(() => {
    if (user) {
      // Set user data in Sentry context
      setSentryUser({
        id: user.id,
        email: user.email,
      });
      
      // Add useful context tags
      Sentry.setTag('authenticated', 'true');
    } else {
      clearSentryUser();
      Sentry.setTag('authenticated', 'false');
    }
  }, [user, setSentryUser, clearSentryUser]);

  // Effect to handle demo migration after successful authentication
  useEffect(() => {
    if (user && user.id && hasPendingDemoMigration()) {
      console.log('User authenticated and demo migration pending, attempting migration...');
      
      const attemptMigration = async () => {
        try {
          // Get the current token for migration
          const currentToken = await getAccessToken();
          if (!currentToken) {
            console.log('No token available for demo migration');
            return;
          }
          
          const success = await migratePendingDemoSession(currentToken);
          if (success) {
            console.log('Demo migration completed successfully');
            // The migration function handles URL cleanup and redirect
            // Show success message
            setSuccess('Your demo work has been saved to your account!');
            // Clear any existing errors
            setError(null);
          } else {
            console.warn('Demo migration failed or was not needed');
          }
        } catch (error) {
          console.error('Demo migration error:', error);
          // Don't show error to user - migration failure shouldn't block normal app usage
          // Just log it for debugging purposes
        }
      };
      
      // Small delay to ensure user state is fully set
      setTimeout(attemptMigration, 500);
    }
  }, [user, hasPendingDemoMigration, migratePendingDemoSession]);
  
  // Initialize impersonation session from localStorage FIRST - before auth check
  useEffect(() => {
    const initializeImpersonationSession = () => {
      const storedSession = ImpersonationManager.getStoredSession();
      if (storedSession) {
        // Check if session is still valid
        const remainingTime = ImpersonationManager.getRemainingTime();
        
        if (remainingTime > 0) {
          console.log(`Restoring impersonation session with ${Math.round(remainingTime / 1000 / 60)} minutes remaining`);
          
          // Restore impersonation context immediately
          setImpersonationContext({
            isImpersonating: true,
            targetUser: storedSession.targetUser,
            originalAdminUser: storedSession.originalAdminUser,
            expiresAt: storedSession.expiresAt,
          });
          
          // Set user context to impersonated user immediately
          setUser(storedSession.targetUser);
          
          // Set timer for remaining time
          const timer = setTimeout(() => {
            console.log('Impersonation session expired (restored from localStorage)');
            stopImpersonation();
          }, remainingTime);
          
          setImpersonationTimer(timer);
        } else {
          // Session has expired
          console.log('Stored impersonation session has expired on startup');
          ImpersonationManager.clearSession();
        }
      }
    };

    // Initialize impersonation BEFORE checking auth to ensure state is set early
    initializeImpersonationSession();
  }, []); // Run only once on mount

  // Handle OAuth errors from URL parameters
  const handleOAuthError = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');
    
    if (error && errorDescription) {
      // Clean up the URL parameters
      const cleanUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
      
      // Parse and display user-friendly error message
      let friendlyMessage = errorDescription;
      
      // Handle PreSignUp errors specifically
      if (errorDescription.includes('PreSignUp failed with error')) {
        if (errorDescription.includes('already exists')) {
          // Extract email and create user-friendly message
          const emailMatch = errorDescription.match(/email\s+([^\s]+)\s+already exists/);
          const email = emailMatch ? emailMatch[1] : 'this email';
          
          if (errorDescription.includes('Please sign in using email/password instead')) {
            friendlyMessage = `An account with ${email} already exists. Please sign in using email/password instead.`;
          } else if (errorDescription.includes('Please sign in with Google')) {
            friendlyMessage = `An account with ${email} already exists. Please sign in with Google instead.`;
          } else {
            friendlyMessage = `An account with ${email} already exists. Please sign in with the method you used to create the account.`;
          }
        }
      }
      
      setError(friendlyMessage);
    }
  };

  // Check if user is already authenticated on mount - after impersonation is restored
  useEffect(() => {
    // Handle any OAuth errors in URL parameters first
    handleOAuthError();
    
    // Small delay to ensure impersonation state is set first
    const timer = setTimeout(() => {
      checkAuth();
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Set up periodic check for session expiry (every 30 seconds)
  useEffect(() => {
    if (impersonationContext.isImpersonating) {
      const interval = setInterval(() => {
        const storedSession = ImpersonationManager.getStoredSession();
        if (!storedSession) {
          console.log('Impersonation session expired (periodic check)');
          stopImpersonation();
        }
      }, 30 * 1000); // Check every 30 seconds
      
      setImpersonationCheckInterval(interval);
      
      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    } else {
      // Clear interval when not impersonating
      if (impersonationCheckInterval) {
        clearInterval(impersonationCheckInterval);
        setImpersonationCheckInterval(null);
      }
    }
  }, [impersonationContext.isImpersonating]);

  // Clean up timers on unmount
  useEffect(() => {
    return () => {
      if (impersonationTimer) {
        clearTimeout(impersonationTimer);
      }
      if (impersonationCheckInterval) {
        clearInterval(impersonationCheckInterval);
      }
    };
  }, []);

  const clearError = () => setError(null);
  const clearSuccess = () => setSuccess(null);

  const handleAuthError = (error: unknown): string => {
    if (error instanceof AuthError) {
      switch (error.name) {
        case 'UserNotConfirmedException':
          return 'Please check your email and confirm your account before signing in.';
        case 'NotAuthorizedException':
          return 'Invalid email or password. Please try again.';
        case 'UserNotFoundException':
          return 'No account found with this email address.';
        case 'InvalidPasswordException':
          return 'Password does not meet requirements.';
        case 'UsernameExistsException':
          return 'An account with this email already exists.';
        case 'LimitExceededException':
          return 'Too many attempts. Please try again later.';
        case 'CodeMismatchException':
          return 'Invalid or expired code. Please try again.';
        case 'ExpiredCodeException':
          return 'This code has expired. Please request a new one.';
        case 'UserLambdaValidationException':
          if (error.message.includes('PostConfirmation failed')) {
            return 'Account confirmation failed due to a backend issue. Please see manual confirmation instructions.';
          }
          if (error.message.includes('PreSignUp failed')) {
            // Check for specific duplicate account messages from our pre-signup lambda
            if (error.message.includes('already exists')) {
              // Extract the email from the error message and create a user-friendly message
              const emailMatch = error.message.match(/email\s+([^\s]+)\s+already exists/);
              const email = emailMatch ? emailMatch[1] : 'this email';
              return `An account with ${email} already exists. Please sign in with Google.`;
            }
            return 'Access denied. Please request access at https://kolega.studio and wait for approval before signing up';
          }
          return error.message || 'A validation error occurred.';
        default:
          return error.message || 'An authentication error occurred.';
      }
    }
    return 'An unexpected error occurred. Please try again.';
  };

  const login = async (email: string, password: string, retryAfterSignOut: boolean = false): Promise<void> => {
    setIsLoading(true);
    setError(null);
    setNeedsConfirmation(null);
    
    try {
      console.log('Starting login process for:', email);

      // Add login attempt breadcrumb for tracking auth flow
      addBreadcrumb('auth', 'Login attempt', 'info', { email });
      const signInResult = await signIn({ 
        username: email, 
        password 
      });

      console.log('Log in result:', signInResult);

      if (signInResult.isSignedIn) {
        console.log('Log in successful, fetching user data...');
        
        // Helper function to determine post-login redirect destination
        const getRedirectDestination = (): string => {
          const from = (location.state as any)?.from;
          const path = from?.pathname || '/select-project';
          const search = from?.search || location.search || '';
          const hash = from?.hash || '';
          return path + search + hash;
        };
        
        // Instead of calling checkAuth, directly get user data to avoid duplicate calls
        try {
          const currentUser = await getCurrentUser();
          
          // Get the actual JWT session first
          const session = await fetchAuthSession();
          
          // Try to fetch user attributes, but handle federated users
          let userAttributes: any = {};
          try {
            userAttributes = await fetchUserAttributes();
          } catch (attributeError) {
            console.warn('Could not fetch user attributes (possibly federated user):', attributeError);
            // For federated users, extract info from JWT ID token
            if (session.tokens?.idToken) {
              try {
                const idTokenPayload = JSON.parse(atob(session.tokens.idToken.toString().split('.')[1]));
                userAttributes = {
                  email: idTokenPayload.email || '',
                  name: idTokenPayload.name || idTokenPayload.given_name || '',
                  email_verified: 'true', // Federated users are pre-verified
                  picture: idTokenPayload?.picture // Profile picture from Google OAuth
                };
              } catch (tokenError) {
                console.warn('Could not parse ID token:', tokenError);
              }
            }
          }

          // Always try to extract picture from ID token (even for non-federated users with Google sign-in)
          if (session.tokens?.idToken && !userAttributes.picture) {
            try {
              const idTokenPayload = JSON.parse(atob(session.tokens.idToken.toString().split('.')[1]));
              if (idTokenPayload.picture) {
                userAttributes.picture = idTokenPayload.picture;
              }
            } catch (tokenError) {
              console.warn('Could not extract picture from ID token:', tokenError);
            }
          }
          const accessToken = session.tokens?.accessToken?.toString();
          
          if (!accessToken) {
            throw new Error('No access token received after login');
          }
          
          // Extract groups from JWT token for admin detection
          let groups: string[] = [];
          
          if (accessToken) {
            try {
              // Parse JWT token to extract groups
              const tokenPayload = JSON.parse(atob(accessToken.toString().split('.')[1]));
              groups = tokenPayload['cognito:groups'] || [];
            } catch (error) {
              console.warn('Could not parse token for groups:', error);
            }
          }


          const userData: User = {
            id: currentUser.userId,
            email: userAttributes.email || '',
            name: userAttributes.name || userAttributes.given_name || currentUser.username,
            username: currentUser.username,
            groups,
            picture: userAttributes?.picture, // Profile picture from Google OAuth
          };

          setUser(userData);
          setToken(accessToken);
          setSentryUser(userData);
          
          // Set cross-domain authentication cookie for kolega.studio
          setAuthCookie({
            isAuthenticated: true,
            userId: userData?.id,
            email: userData?.email,
            displayName: userData?.name || userData?.email
          });
          
          // Track successful login in Mixpanel
          MixpanelService.identify(userData?.id);
          MixpanelService.track(EventName.USER_LOGIN, {
            email: userData?.email,
            userId: userData?.id,
            name: userData?.name,
          });

          MixpanelService.setUserProperties({
            $name: userData?.name,
            $email: userData?.email,
            $userId: userData?.id,
            $created: new Date().toISOString()
          });
          
          console.log('User data set successfully, navigating to intended destination');
          
          const destination = getRedirectDestination();
          sessionStorage.setItem('postLoginDestination', destination);
          navigate(destination, { replace: true });
        } catch (userError) {
          console.error('Error fetching user data after Log in:', userError);
          // Fallback to checkAuth if direct method fails
          const authSuccess = await checkAuth();
          if (authSuccess) {
            const destination = getRedirectDestination();
            sessionStorage.setItem('postLoginDestination', destination);
            navigate(destination, { replace: true });
          }
        }
      } else {
        console.log('Log in not completed, checking next steps:', signInResult);
        
        // Check if user needs confirmation
        if (signInResult.nextStep && signInResult.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
          console.log('User needs to confirm their account');
          setNeedsConfirmation(email);
          setError('Your account is not confirmed. Please check your email for the confirmation code, or we can send you a new one.');
          // Don't throw an error for confirmation case - this is expected behavior
          return;
        } 
        // Handle MFA challenges
        else if (signInResult.nextStep && signInResult.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
          console.log('MFA TOTP challenge required');
          setMfaChallenge({
            isRequired: true,
            challengeType: 'TOTP'
          });
          return;
        }
        else if (signInResult.nextStep && signInResult.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
          console.log('MFA SMS challenge required');
          setMfaChallenge({
            isRequired: true,
            challengeType: 'SMS'
          });
          return;
        }
        else if (signInResult.nextStep && signInResult.nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
          console.log('MFA setup required');
          setError('MFA setup is required. Please contact support.');
          return;
        }
        else {
          console.log('Unknown log in step:', signInResult.nextStep);
          setError('Login was not completed. Please try again.');
        }
      }
    } catch (error) {
      console.error('Login error:', error);

      // Track authentication errors in Sentry
      // Ensure we have a proper Error instance before sending to Sentry
      const errorToCapture = error instanceof Error ? 
          error : 
          new Error(typeof error === 'string' ? error : 'Unknown authentication error');
          
      captureError(errorToCapture, {
        tags: {
          component: 'AuthContext',
          action: 'login',
          errorType: error instanceof Error ? error.name : 'UnknownError'
        },
        extra: {
          email,
          hasPassword: !!password,
          retryAfterSignOut
        }
      });
      
      if (error instanceof AuthError) {
        console.log('Auth error type:', error.name, 'Message:', error.message);
        
        if (error.name === 'UserNotConfirmedException') {
          setNeedsConfirmation(email);
          setError('Your account is not confirmed. Please check your email for the confirmation code, or we can send you a new one.');
          // Don't re-throw for confirmation case
          return;
        } else if (error.name === 'UserAlreadyAuthenticatedException' && !retryAfterSignOut) {
          // Handle the edge case where Cognito thinks user is already signed in
          console.log('UserAlreadyAuthenticatedException detected, forcing sign out and retrying...');
          try {
            // Force a sign out to clear Cognito state
            await signOut();
            console.log('Forced sign out complete, retrying login...');
            // Retry login once after sign out (with retryAfterSignOut flag to prevent infinite loop)
            return await login(email, password, true);
          } catch (signOutError) {
            console.error('Error during forced sign out:', signOutError);
            // Continue to general error handling
          }
        } else if (error.name === 'NotAuthorizedException' && !retryAfterSignOut) {
          // Handle the case where a user is technically signed in but session is invalid
          console.log('Not authorized, trying to sign out first and retry...');
          try {
            await signOut();
            return login(email, password, true);
          } catch (signOutErr) {
            console.error('Error during sign-out before retry:', signOutErr);
            // Create a proper Error instance from unknown error
            const error = signOutErr instanceof Error ? 
              signOutErr : 
              new Error(typeof signOutErr === 'string' ? signOutErr : 'Sign out failed');
            
            captureError(error, {
              tags: {
                component: 'AuthContext',
                action: 'signOutBeforeRetry'
              }
            });
            setError('Authentication failed. Please check your email and password.');
          }
        } else if (error.name === 'PasswordResetRequiredException') {
          // User needs to reset password
          setError('Password reset required. Please use the "Forgot Password" option.');
        } else if (error.name === 'UserNotFoundException') {
          // User not found
          setError('We couldn\'t find an account with that email. Please check your email or register.');
        } else {
          // Use handleAuthError for other auth errors
          const errorMessage = handleAuthError(error);
          setError(errorMessage);
        }
      } else {
        console.error('Non-Auth error:', error);
        setError('An unexpected error occurred. Please try again.');
      }
      
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (email: string, password: string): Promise<{ requiresConfirmation: boolean }> => {
    setIsLoading(true);
    setError(null);
    setNeedsConfirmation(null); // Clear any previous confirmation state
    
    try {
      console.log('Starting registration for:', email);
      
      // Track registration started
      MixpanelService.track(EventName.USER_REGISTRATION_STARTED, {
        email
      });
      
      const { nextStep } = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email: email,
          },
        },
      });

      console.log('Registration nextStep:', nextStep);
      if (nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
        console.log('Registration requires confirmation, setting needsConfirmation to:', email);
        // Set the needsConfirmation state directly here
        setNeedsConfirmation(email);
        
        // Track successful registration (pending confirmation)
        MixpanelService.track(EventName.USER_SIGN_UP, {
          email,
          requiresConfirmation: true
        });
        
        return { requiresConfirmation: true };
      }
      // Track successful registration (no confirmation needed)
      MixpanelService.track(EventName.USER_SIGN_UP, {
        email,
        requiresConfirmation: false
      });
      
      return { requiresConfirmation: false };
    } catch (error) {
      const errorMessage = handleAuthError(error);
      setError(errorMessage);
      console.error('Registration error:', error);
      
      // Track registration failure
      MixpanelService.track(EventName.USER_REGISTRATION_FAILED, {
        email,
        errorType: error instanceof AuthError ? error.name : 'Unknown',
        errorMessage: error instanceof Error ? error.message : 'Unknown error'
      });
      
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const clearMFAChallenge = () => {
    setMfaChallenge(null);
  };

  const confirmRegistration = async (email: string, code: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    console.log('Starting confirmation process for:', email, 'with code length:', code.length);
    
    try {
      const { isSignUpComplete } = await confirmSignUp({
        username: email,
        confirmationCode: code,
      });

      console.log('Confirmation result:', { isSignUpComplete });

      if (isSignUpComplete) {
        console.log('Account confirmed successfully');
        // Track account confirmation
        MixpanelService.track(EventName.USER_ACCOUNT_CONFIRMED, {
          email: email,
          userId: user?.id,
        });
        
        setNeedsConfirmation(null);
        setSuccess('Account confirmed successfully! You can now log in.');
        // After confirmation, stay on the same page but hide confirmation form
        return;
      } else {
        console.log('Confirmation did not complete but no error was thrown');
        setError('Confirmation did not complete. Please try again.');
      }
    } catch (error) {
      console.error('Confirmation error:', error);
      
      if (error instanceof AuthError && error.name === 'UserLambdaValidationException' && error.message.includes('PostConfirmation failed')) {
        // The confirmation code was correct, but PostConfirmation Lambda failed
        // This means the user IS confirmed, despite the error
        console.log('PostConfirmation trigger failed, but user should be confirmed');
        
        setNeedsConfirmation(null);
        setSuccess('Account confirmed successfully! The confirmation code was valid. You can now log in.');
        // Don't throw error - user is actually confirmed
        return;
      } else if (error instanceof AuthError && error.name === 'CodeMismatchException') {
        console.log('Invalid confirmation code entered');
        setError('Invalid confirmation code. Please check your email and try again.');
      } else {
        const errorMessage = handleAuthError(error);
        console.log('Other confirmation error:', errorMessage);
        setError(errorMessage);
      }
      // Don't throw the error, just handle it locally
    } finally {
      setIsLoading(false);
    }
  };

  const resendConfirmation = async (email: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    try {
      await resendSignUpCode({ username: email });
      setSuccess('A new confirmation code has been sent to your email.');
      
      // Track confirmation code resent
      MixpanelService.track(EventName.CONFIRMATION_CODE_RESENT, {
        email
      });
    } catch (error) {
      const errorMessage = handleAuthError(error);
      setError(errorMessage);
      console.error('Resend confirmation error:', error);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    setIsLoading(true);
    try {
      // Clear impersonation timers and localStorage
      if (impersonationTimer) {
        clearTimeout(impersonationTimer);
        setImpersonationTimer(null);
      }
      if (impersonationCheckInterval) {
        clearInterval(impersonationCheckInterval);
        setImpersonationCheckInterval(null);
      }
      
      // Track logout event before clearing user data
      if (user) {
        MixpanelService.track(EventName.USER_LOGOUT, {
          userId: user?.id,
          email: user?.email,
        });
      }
      
      // Clear impersonation session from localStorage
      ImpersonationManager.clearSession();
      
      // Clear cross-domain authentication cookie
      clearAuthCookie();
      
      await signOut();
      
      setUser(null);
      setToken(null);
      setError(null);
      clearSentryUser();
      
      // Clear impersonation state
      setImpersonationContext({ isImpersonating: false });
      
      ImpersonationManager.clearSession();
      MixpanelService.reset(); // Reset Mixpanel identity even on error
      navigate('/#login');
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, clear local state
      setUser(null);
      setToken(null);
      setError(null);
      setImpersonationContext({ isImpersonating: false });
      ImpersonationManager.clearSession();
      clearAuthCookie(); // Clear cross-domain auth cookie even on error
      MixpanelService.reset(); // Reset Mixpanel identity even on error
      navigate('/#login');
    } finally {
      setIsLoading(false);
    }
  };

  const getAccessToken = async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();
      
      if (accessToken) {
        // Keep token in sync with the current session
        setToken(accessToken);
        return accessToken;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching access token:', error);
      setToken(null);
      return null;
    }
  };

  const checkAuth = async (skipLoading = false): Promise<boolean> => {
    if (!skipLoading) {
      setIsLoading(true);
    }
    
    try {
      console.log('Checking authentication status...');
      
      // Add breadcrumb for auth check
      addBreadcrumb('auth', 'Checking authentication status', 'info', {});
      
      const currentUser = await getCurrentUser();
      
      console.log('Current user found:', currentUser.username);
      
      // Get the actual JWT session first
      const session = await fetchAuthSession();
      
      // Try to fetch user attributes, but handle federated users with insufficient scopes
      let userAttributes: any = {};
      try {
        userAttributes = await fetchUserAttributes();
      } catch (attributeError) {
        console.warn('Could not fetch user attributes (possibly federated user with insufficient scopes):', attributeError);
        // For federated users, extract info from JWT ID token
        if (session.tokens?.idToken) {
          try {
            const idTokenPayload = JSON.parse(atob(session.tokens.idToken.toString().split('.')[1]));
            userAttributes = {
              email: idTokenPayload.email || '',
              name: idTokenPayload.name || idTokenPayload.given_name || '',
              email_verified: idTokenPayload.email_verified || 'true', // Federated users are pre-verified
              picture: idTokenPayload?.picture // Profile picture from Google OAuth
            };
          } catch (tokenError) {
            console.warn('Could not parse ID token:', tokenError);
          }
        }
      }

      // Always try to extract picture from ID token (even for non-federated users with Google sign-in)
      if (session.tokens?.idToken && !userAttributes.picture) {
        try {
          const idTokenPayload = JSON.parse(atob(session.tokens.idToken.toString().split('.')[1]));
          if (idTokenPayload.picture) {
            userAttributes.picture = idTokenPayload.picture;
          }
        } catch (tokenError) {
          console.warn('Could not extract picture from ID token:', tokenError);
        }
      }
      const accessToken = session.tokens?.accessToken?.toString();
      
      if (!accessToken) {
        console.log('No access token found');
        setUser(null);
        setToken(null);
        return false;
      }
      
      // Extract groups from JWT token for admin detection
      let groups: string[] = [];
      
      if (accessToken) {
        try {
          // Parse JWT token to extract groups
          const tokenPayload = JSON.parse(atob(accessToken.toString().split('.')[1]));
          groups = tokenPayload['cognito:groups'] || [];
        } catch (error) {
          console.warn('Could not parse token for groups:', error);
        }
      }


      // Create user object from Cognito data
      const userData: User = {
        id: currentUser.userId,
        email: userAttributes.email || '',
        name: userAttributes.name || userAttributes.given_name || currentUser.username,
        username: currentUser.username,
        groups,
        picture: userAttributes?.picture, // Profile picture from Google OAuth
      };

      setUser(userData);
      setToken(accessToken);
      
      // Set cross-domain authentication cookie for kolega.studio
      setAuthCookie({
        isAuthenticated: true,
        userId: userData?.id,
        email: userData?.email,
        displayName: userData?.name || userData?.email || null,
      });
      
      try {
        MixpanelService.identify(userData.id);
        MixpanelService.setUserProperties({
          $name: userData.name,
          $email: userData.email,
          $userId: userData.id,
          $created: new Date().toISOString()
        });
      } catch (error) {
        console.error('Failed to set Mixpanel user properties:', error);
      }

      setSentryUser(userData);
      
      console.log('Authentication check successful with JWT token');
      return true;
    } catch (error) {
      console.log('User not authenticated:', error);
      setUser(null);
      setToken(null);
      return false;
    } finally {
      if (!skipLoading) {
        setIsLoading(false);
      }
    }
  };

  // Function to update needsConfirmation state directly
  const updateNeedsConfirmation = (email: string | null) => {
    console.log('Updating needsConfirmation state to:', email);
    setNeedsConfirmation(email);
  };

  // Helper function to check if current user is admin
  const isAdmin = user?.groups?.includes('admin') || false;

  // Helper functions for impersonation time management (using centralized manager)
  const getImpersonationTimeRemaining = (): number => {
    return ImpersonationManager.getRemainingTime();
  };

  const getImpersonationExpiryTime = (): Date | null => {
    return ImpersonationManager.getExpiryDate();
  };

  // WebSocket management for impersonation changes
  const registerWebSocketDisconnectCallback = (callback: () => void): (() => void) => {
    webSocketDisconnectCallbacks.current.add(callback);
    console.log(`Registered WebSocket disconnect callback. Total callbacks: ${webSocketDisconnectCallbacks.current.size}`);
    
    // Return unregister function
    return () => {
      webSocketDisconnectCallbacks.current.delete(callback);
      console.log(`Unregistered WebSocket disconnect callback. Total callbacks: ${webSocketDisconnectCallbacks.current.size}`);
    };
  };

  // Impersonation functions with localStorage persistence
  const startImpersonation = (targetUserId: string, targetUser: User) => {
    if (!user || !isAdmin) {
      console.warn('Cannot start impersonation: user is not an admin');
      return;
    }

    // Clear any existing timers
    if (impersonationTimer) {
      clearTimeout(impersonationTimer);
    }
    if (impersonationCheckInterval) {
      clearInterval(impersonationCheckInterval);
    }

    const now = Date.now();
    const expiresAt = now + IMPERSONATION_DURATION_MS;

    // Create session object for localStorage
    const session: StoredImpersonationSession = {
      targetUser,
      originalAdminUser: user,
      expiresAt,
      startedAt: now,
    };

    // Store session in localStorage
    ImpersonationManager.setSession(session);

    // Update context state
    setImpersonationContext({
      isImpersonating: true,
      targetUser,
      originalAdminUser: user,
      expiresAt,
    });

    // Set the user context to the impersonated user
    setUser(targetUser);
    
    // Update cross-domain auth cookie to reflect impersonated user
    setAuthCookie({
      isAuthenticated: true,
      userId: targetUser?.id,
      email: targetUser?.email,
      displayName: targetUser?.name || targetUser?.email || null
    });
    
    // Set auto-stop timer for full duration
    const timer = setTimeout(() => {
      console.log('Impersonation session expired (30 minutes)');
      stopImpersonation();
    }, IMPERSONATION_DURATION_MS);
    
    setImpersonationTimer(timer);
    
    console.log(`Admin ${user.email} started impersonating ${targetUser.email} (expires at ${new Date(expiresAt).toLocaleTimeString()})`);
  };

  const stopImpersonation = () => {
    // Clear timers
    if (impersonationTimer) {
      clearTimeout(impersonationTimer);
      setImpersonationTimer(null);
    }
    if (impersonationCheckInterval) {
      clearInterval(impersonationCheckInterval);
      setImpersonationCheckInterval(null);
    }

    // Get original admin user BEFORE clearing localStorage
    let originalAdmin = impersonationContext?.originalAdminUser;

    if (!originalAdmin) {
      const storedSession = ImpersonationManager.getStoredSession();
      originalAdmin = storedSession?.originalAdminUser;
    }

    // Clear localStorage AFTER retrieving original admin
    ImpersonationManager.clearSession();

    // Disconnect WebSockets
    console.log(`Disconnecting ${webSocketDisconnectCallbacks.current.size} WebSocket connections due to impersonation stop`);
    webSocketDisconnectCallbacks.current.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('Error calling WebSocket disconnect callback:', error);
      }
    });

    if (!originalAdmin) {
      console.error('Cannot stop impersonation: no original admin user found');
      logout();
      return;
    }
    
    // Restore original admin user
    setUser(originalAdmin);
    
    // Update cross-domain auth cookie to reflect original admin user
    setAuthCookie({
      isAuthenticated: true,
      userId: originalAdmin?.id,
      email: originalAdmin?.email,
      displayName: originalAdmin?.name || originalAdmin?.email || null
    });
    
    // Clear impersonation context
    setImpersonationContext({
      isImpersonating: false,
    });

    localStorage.removeItem('selectedProjectPath');
    localStorage.removeItem('workspaceId');
    console.log(`Stopped impersonating, restored admin user ${originalAdmin.email}`);

    // Force a full page reload to /select-project to ensure all state is cleared
    window.location.href = '/select-project';
  };

  // MFA Functions
  const verifyMFACode = async (code: string): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      
      const result = await confirmSignIn({ challengeResponse: code });
      
      if (result.isSignedIn) {
        // Clear MFA challenge state
        setMfaChallenge(null);
        
        // Complete authentication process
        await checkAuth();
        
        // Track successful MFA verification
        MixpanelService.track(EventName.MFA_VERIFICATION_SUCCEEDED, {
          userId: user?.id,
          email: user?.email,
          type: mfaChallenge?.challengeType || 'TOTP'
        });
        
        // Navigate to intended destination
        const from = (location.state as any)?.from?.pathname || '/select-project';
        navigate(from, { replace: true });
      } else {
        setError('MFA verification failed. Please try again.');
        
        // Track failed MFA verification
        MixpanelService.track(EventName.MFA_VERIFICATION_FAILED, {
          userId: user?.id,
          email: user?.email,
          type: mfaChallenge?.challengeType || 'TOTP',
          reason: 'Verification incomplete'
        });
      }
    } catch (error) {
      console.error('MFA verification error:', error);
      if (error instanceof AuthError) {
        setError(handleAuthError(error));
      } else {
        setError('Failed to verify MFA code. Please try again.');
      }
      
      // Track failed MFA verification with error
      MixpanelService.track(EventName.MFA_VERIFICATION_FAILED, {
        userId: user?.id,
        email: user?.email,
        type: mfaChallenge?.challengeType || 'TOTP',
        errorType: error instanceof AuthError ? error.name : 'Unknown',
        errorMessage: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const setupTOTP = async (): Promise<{ secretKey: string; qrCodeUri: string }> => {
    try {
      // Track MFA setup started
      MixpanelService.track(EventName.MFA_SETUP_STARTED, {
        userId: user?.id,
        email: user?.email
      });
      
      const setupDetails = await setUpTOTP();
      
      const userEmail = user?.email || 'user';
      const qrCodeUri = setupDetails.getSetupUri('KolegaPlatform', userEmail).href;
      
      return {
        secretKey: setupDetails.sharedSecret,
        qrCodeUri
      };
    } catch (error) {
      console.error('Error setting up TOTP:', error);
      
      // Track MFA setup failure
      MixpanelService.track(EventName.MFA_VERIFICATION_FAILED, {
        userId: user?.id,
        email: user?.email,
        stage: 'setup',
        errorType: error instanceof AuthError ? error.name : 'Unknown',
        errorMessage: error instanceof Error ? error.message : 'Unknown error'
      });
      
      throw new Error('Failed to set up two-factor authentication. Please try again.');
    }
  };

  const completeTOTPSetup = async (code: string): Promise<void> => {
    try {
      await verifyTOTPSetup({ code });
      
      // Set TOTP as preferred MFA method
      await updateMFAPreference({ totp: 'PREFERRED' });
      
      // Track successful MFA setup completion
      MixpanelService.track(EventName.MFA_SETUP_COMPLETED, {
        userId: user?.id,
        email: user?.email,
        type: 'TOTP'
      });
    } catch (error) {
      console.error('Error completing TOTP setup:', error);
      
      // Track failed MFA setup completion
      MixpanelService.track(EventName.MFA_VERIFICATION_FAILED, {
        userId: user?.id,
        email: user?.email,
        stage: 'setup_verification',
        errorType: error instanceof AuthError ? error.name : 'Unknown',
        errorMessage: error instanceof Error ? error.message : 'Unknown error'
      });
      
      throw new Error('Invalid verification code. Please try again.');
    }
  };

  const getMFAStatus = async (): Promise<{ enabled: boolean; preferred: boolean }> => {
    try {
      const preference = await fetchMFAPreference();
      
      return {
        enabled: preference.enabled?.includes('TOTP') || false,
        preferred: preference.preferred === 'TOTP'
      };
    } catch (error) {
      console.error('Error getting MFA status:', error);
      return { enabled: false, preferred: false };
    }
  };

  const disableMFA = async (): Promise<void> => {
    try {
      await updateMFAPreference({
        totp: 'DISABLED'
      });

      // Verify the MFA status was updated correctly
      const updatedStatus = await getMFAStatus();
      if (updatedStatus.enabled) {
        throw new Error('Failed to disable MFA. The status could not be updated.');
      }
    } catch (error) {
      console.error('Error disabling MFA:', error);
      throw error instanceof Error ? error : new Error('Failed to disable MFA. Please try again.');
    }
  };

  // Helper function to get a human-friendly display name
  const displayName = (): string | null => {
    if (!user) return null;
    
    // If name exists and is not a UUID, use it
    if (user.name && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(user.name)) {
      return user?.name?.trim();
    }
    
    // Fallback to email username if available
    if (user.email) {
      return user?.email?.split('@')[0]?.trim();
    }
    
    return null;
  };

  // Password reset functions
  const requestPasswordReset = async (email: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      await resetPassword({ username: email });
      setSuccess('Password reset code sent! Please check your email.');
      
      // Track password reset request
      MixpanelService.track(EventName.PASSWORD_RESET_REQUESTED, {
        email: email,
        userId: user?.id,
      });
    } catch (error) {
      console.error('Password reset request error:', error);
      const errorMessage = handleAuthError(error);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmPasswordReset = async (email: string, code: string, newPassword: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword: newPassword,
      });
      
      // Track password reset completion
      MixpanelService.track(EventName.PASSWORD_RESET_COMPLETED, {
        email: email,
        userId: user?.id,
      });
      
      setSuccess('Password reset successfully! You can now log in with your new password.');
    } catch (error) {
      console.error('Password reset confirmation error:', error);
      const errorMessage = handleAuthError(error);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Federated Sign-In function (Google, etc.)
  const signInWithProvider = async (provider: 'Google'): Promise<void> => {
    setIsLoading(true);
    setError(null);
    setNeedsConfirmation(null);
    
    try {
      console.log(`Starting federated sign-in with ${provider}`);
      
      // Add login attempt breadcrumb for tracking auth flow
      addBreadcrumb('auth', `${provider} federated login attempt`, 'info', { provider });
      
      // Use Amplify's built-in federated sign-in
      // This will redirect to Google OAuth and then back to Cognito
      await signInWithRedirect({ provider });
      
      // Note: After redirect, the user will be handled by checkAuth() on app load
      
    } catch (error) {
      console.error(`${provider} federated login error:`, error);
      
      // Track authentication errors in Sentry
      const errorToCapture = error instanceof Error ? 
          error : 
          new Error(typeof error === 'string' ? error : `Unknown ${provider} authentication error`);
          
      captureError(errorToCapture, {
        tags: {
          component: 'AuthContext',
          action: 'signInWithProvider',
          errorType: error instanceof Error ? error.name : 'UnknownError'
        },
        extra: {
          provider
        }
      });
      
      setError(`${provider} sign-in failed. Please try again.`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    user,
    setUser,
    token,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    confirmRegistration,
    resendConfirmation,
    logout,
    checkAuth,
    getAccessToken,
    error,
    clearError,
    success,
    clearSuccess,
    needsConfirmation,
    updateNeedsConfirmation,
    
    // Impersonation functionality
    impersonationContext,
    startImpersonation,
    stopImpersonation,
    isAdmin,
    getImpersonationTimeRemaining,
    getImpersonationExpiryTime,
    
    // WebSocket management for impersonation
    registerWebSocketDisconnectCallback,
    
    // MFA functionality
    mfaChallenge,
    verifyMFACode,
    setupTOTP,
    completeTOTPSetup,
    getMFAStatus,
    disableMFA,
    clearMFAChallenge,
    // Password reset functionality
    requestPasswordReset,
    confirmPasswordReset,
    // Federated Sign-In functionality
    signInWithProvider,
    // Display name helper
    displayName,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
